import { useRef, useState } from 'react';
import { ModalTransactions } from 'components/Modal/ModalTransaction/ModalTransaction';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn } from 'redux/selectors';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useFabPosition } from 'hooks/useFabPosition';
import {
  Calendar,
  DollarSign,
  Home,
  PieChart,
  Plus,
  Wallet,
} from 'lucide-react';

export default function Footer({ isDark = false }) {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [modalOpen, setModalOpen] = useState(false);
  const navRef = useRef(null);
  const footerRef = useRef(null);
  const fabBottomPx = useFabPosition({
    navRef,
    footerRef,
    enabled: isLoggedIn,
  });
  const toggleModal = () => {
    setModalOpen(prevState => !prevState);
  };
  const { t } = useTranslation();
  const navItems = [
    { to: '/home', icon: Home, label: t('pageName.home') },
    { to: '/reports', icon: PieChart, label: t('pageName.report') },
    { to: '/planning', icon: Calendar, label: t('pageName.planning') },
    { to: '/wallets', icon: Wallet, label: t('pageName.wallets') },
  ];

  return (
    <>
      {isLoggedIn && (
        <>
          <div
            ref={navRef}
            className={`fixed bottom-0 left-0 z-[250] w-full border-t px-4 pt-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur md:hidden ${
              isDark
                ? 'border-slate-800 bg-slate-900/95'
                : 'border-slate-200 bg-white/95'
            } pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]`}
          >
            <div className="mx-auto grid max-w-md grid-cols-4 items-end">
              {navItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 text-[11px] ${
                      isActive
                        ? 'text-[#FF7601]'
                        : isDark
                        ? 'text-slate-400'
                        : 'text-slate-500'
                    }`
                  }
                >
                  <Icon size={22} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
          <button
            onClick={toggleModal}
            style={
              fabBottomPx != null ? { bottom: `${fabBottomPx}px` } : undefined
            }
            className="group fixed right-5 z-[260] flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#FF8A1F] to-[#FF7601] text-white shadow-[0_12px_28px_rgba(255,118,1,0.42)] transition-[transform,box-shadow,bottom] duration-200 hover:scale-105 hover:shadow-[0_16px_34px_rgba(255,118,1,0.55)] max-md:bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:bottom-6 md:right-6"
            type="button"
            aria-label="Add transaction"
          >
            <span className="pointer-events-none absolute inset-0 rounded-full ring-4 ring-orange-200/60 transition group-hover:ring-orange-200"></span>
            <span className="fab-icon-stack">
              <Plus className="fab-plus" size={26} strokeWidth={2.5} />
              <DollarSign className="fab-dollar" size={24} strokeWidth={2.5} />
            </span>
          </button>
          {modalOpen && <ModalTransactions onClose={toggleModal} />}
        </>
      )}
      <footer
        ref={isLoggedIn ? footerRef : undefined}
        className={`mt-auto w-full shrink-0 border-t px-4 py-6 backdrop-blur md:mt-12 ${
          isLoggedIn ? 'md:pl-72' : ''
        } ${
          isDark
            ? 'border-slate-800 bg-slate-900/60'
            : 'border-slate-200 bg-white/70'
        } ${
          isLoggedIn
            ? 'max-md:pb-[calc(5rem+env(safe-area-inset-bottom,0px))]'
            : ''
        }`}
      >
        <div
          className={`mx-auto flex w-full max-w-4xl flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm xl:max-w-6xl 2xl:max-w-7xl ${
            isDark ? 'text-slate-300' : 'text-slate-600'
          }`}
        >
          <p
            className={`m-0 shrink-0 ${
              isDark ? 'text-slate-100' : 'text-slate-900'
            }`}
          >
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <NavLink
            to="/support"
            className="min-w-0 shrink-0 whitespace-nowrap font-medium text-[#FF7601] transition hover:opacity-80"
          >
            {t('footer.contactSupport')}
          </NavLink>
        </div>
      </footer>
    </>
  );
}
